import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Link,
  Alert,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useTranslation } from 'react-i18next';

const CustomerLoginPageV2: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, isAuthenticated, loading } = useCustomerAuth();
  const { t, i18n } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Estado propio del botón de Google: el flujo dura lo que tarde el usuario en
  // dar el consentimiento en el popup, así que no puede compartir spinner con el
  // envío del formulario.
  const [googleLoading, setGoogleLoading] = useState(false);

  // Automatic redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate('/customer/plans');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email: formData.email, password: formData.password });
      // Login successful - wait for state update before navigation
      // Small delay before navigation to ensure state updates
      setTimeout(() => {
        navigate('/customer/plans');
      }, 100);
    } catch (err: any) {
      // El contexto lanza un PortalLoginError con el mensaje del contrato F1 ya
      // traducido (credenciales + intentos restantes, bloqueo con minutos,
      // cuenta inactiva). Antes se leía `err.response.data.detail`, que en un
      // login por fetch NUNCA existía: siempre se veía el mensaje genérico.
      setError(err?.message || t('customer.auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  // «Entrar con Google»: el mismo SSO que WAI y que el empleado IA (Composio a
  // través del auth-service global). El flujo entero —popup + sondeo— vive en
  // utils/portalGoogleLogin para que los dos portales se comporten igual.
  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      setTimeout(() => {
        navigate('/customer/plans');
      }, 100);
    } catch (err: any) {
      // PortalLoginError ya trae el mensaje del contrato traducido (no
      // habilitado para Google, cuenta bloqueada, popup bloqueado, tiempo…).
      setError(err?.message || t('customer.auth.loginError'));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#f8f9fa'
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: '#1a1a1a'
              }}
            >
              {t('customer.auth.welcomeBack')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customer.auth.loginSubtitle')}
            </Typography>
          </Box>

          {/* Language Selector */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('customer.auth.language')}</InputLabel>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              label={t('customer.auth.language')}
            >
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('customer.auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('customer.auth.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderRadius: 2,
                boxShadow: 'none'
              }}
            >
              {isLoading ? t('customer.auth.loggingIn') : t('auth.login')}
            </Button>
          </form>

          {/* SSO: misma vía que el resto de WAI — el marketplace no registra
              ninguna app de Google, el consentimiento lo sirve Composio a través
              del auth-service global. */}
          <Divider sx={{ mt: 3, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
              {t('portalAuth.or', 'o')}
            </Typography>
          </Divider>
          <Button
            onClick={handleGoogleLogin}
            fullWidth
            variant="outlined"
            size="large"
            disabled={googleLoading || isLoading}
            startIcon={googleLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
            sx={{ mt: 1, py: 1.25, fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
            data-testid="customer-login-google-btn"
          >
            {googleLoading
              ? t('portalAuth.googleConnecting', 'Conectando con Google...')
              : t('portalAuth.googleSignIn', 'Entrar con Google')}
          </Button>

          {/* Footer Links */}
          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              {t('customer.auth.noAccount')}{' '}
              <Link
                onClick={() => navigate('/customer/register')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {t('customer.auth.signUp')}
              </Link>
            </Typography>
          </Box>
        </Paper>

        {/* Back to Home */}
        <Box textAlign="center" mt={3}>
          <Link
            onClick={() => navigate('/customer')}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            ← {t('customer.auth.backToHome')}
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerLoginPageV2;

